// src/pages/account/Login.js
import React, { useState, useContext, useCallback, useRef, useEffect, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import './Login.css';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { useBanner } from '../../context/BannerContext';
import { useNavigate, Link, useLocation } from 'react-router-dom'; // Import Link and useLocation
import { apiFetch } from '../../utils/apiClient'; // Import apiFetch
import { BASE_URL } from '../../utils/config';
import { useRecaptcha } from '../../hooks/useRecaptcha'; // Import the hook
import { useLanguage } from '../../hooks/useLanguage';
import TwoFactorAuth from '../../components/verification/TwoFactorAuth'; // Import the new 2FA component
import googleLogo from '../../assets/images/google-logo.svg';

const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));

const Login = () => {
  const { t } = useTranslation();
  // --- Pre-rendering check ---
  const isReactSnap = navigator.userAgent.includes('ReactSnap');

  // --- 2FA State ---
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);
  const [twoFactorEmailMasked, setTwoFactorEmailMasked] = useState(null);
  const [twoFactorPhoneNumber, setTwoFactorPhoneNumber] = useState(null);
  const [hasAuthenticator, setHasAuthenticator] = useState(false);
  const [twoFactorSetupToken, setTwoFactorSetupToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false); // New state for password visibility
  const [isSubmitting, setIsSubmitting] = useState(false); // The login function from UserContext will handle all state updates.
  const { login } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext); // Get addNotification from context
  const { addBanner } = useBanner();
  const navigate = useNavigate();
  const location = useLocation(); // Get location object
  const handleVerifyRef = useRef(null); // Create a ref to hold the callback
  const lang = useLanguage();
  const formRef = useRef(null); // Ref for the form element
  const [error, setError] = useState(null);

  // If an error was set from an async callback, throw it during render to be caught by the Error Boundary
  if (error) {
    throw error;
  }

  // New useEffect to handle the notification from email verification
  useEffect(() => {
    if (location.state?.emailVerified) {
      addNotification(t('loginPage.emailVerifiedSuccess'), 'success', 5000);
      // Clean up state to prevent notification on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, addNotification, navigate, location.pathname, t]);

  // Effect to handle error messages from OAuth redirects
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');

    if (error === 'age_restriction') {
      addNotification(t('loginPage.googleAgeRestrictionError'), 'error', 0, true);
      navigate(`/${lang}/login`, { replace: true }); // Clean the URL
    } else if (error === 'authentication_failed') {
      addNotification(t('loginPage.googleAuthFailedError'), 'error', 5000);
      navigate(`/${lang}/login`, { replace: true }); // Clean the URL
    }
    // This effect should only run when the search part of the URL changes.
  }, [location.search, addNotification, navigate, t, lang]);

  // --- Shared Success Handler ---
  const handleLoginSuccess = useCallback(
    async (loginData) => {
      login(loginData); // Use the centralized login function from the context

      // After successful login, check for pending verifications sequentially.
      if (!loginData.user.isEmailVerified) {
        const userEmail = loginData.user.email;
        const handleResendVerification = async () => {
          try {
            const res = await apiFetch(
              `${BASE_URL}/authUser/request-verification-email`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: userEmail }),
              },
              loginData.accessToken // Use the new token for this request
            );
            const resData = await res.json();
            if (res.ok) {
              addNotification(t('loginPage.resendEmailSuccess', { email: userEmail }), 'success', 5000);
            } else {
              addNotification(t('loginPage.resendEmailError', { error: resData.message || 'Error' }), 'error', 5000);
            }
          } catch (err) {
            addNotification(t('loginPage.resendEmailNetworkError'), 'error', 5000);
          }
        };
        addBanner(t('loginPage.verifyEmailBanner', { email: userEmail }), 'warning', [
          { text: t('loginPage.resendEmailButton'), onClick: handleResendVerification },
          { text: t('loginPage.goToAccountButton'), onClick: () => navigate(`/${lang}/preferences`) },
        ]);
      } else if (!loginData.user.isPhoneVerified) {
        addBanner(t('loginPage.verifyPhoneBanner'), 'warning', [{ text: t('loginPage.goToMyAccountButton'), onClick: () => navigate(`/${lang}/preferences`) }]);
      } else {
        addNotification(t('loginPage.loginSuccess'), 'success', 2000);
      }

      const returnToPropertyId = localStorage.getItem('returnToPropertyId');
      if (returnToPropertyId) {
        localStorage.removeItem('returnToPropertyId');
        addNotification(t('loginPage.returningToProperty'), 'info', 2000);
        navigate(`/${lang}/map?propertyId=${returnToPropertyId}`);
      } else {
        navigate(`/${lang}`);
      }
    },
    [addNotification, navigate, login, addBanner, t, lang]
  );

  // Define recaptcha handlers that don't depend on `reset` yet
  const handleRecaptchaExpire = useCallback(() => {
    setIsSubmitting(false);
    addNotification(t('recaptcha.expired'), 'warning');
  }, [addNotification, t]);

  const handleRecaptchaError = useCallback(
    (err) => {
      setIsSubmitting(false);
      addNotification(t('recaptcha.loadError'), 'error', 7000);
      setError(new Error(t('recaptcha.loadError'))); // Propagate error
    },
    [addNotification, t, setError]
  );

  // Get `reset` from the hook
  const { recaptchaRef, execute, onChange, reset, onErrored, onExpired } = useRecaptcha((token) => handleVerifyRef.current(token), handleRecaptchaExpire, handleRecaptchaError);

  // Now define handleVerify, with `reset` in its dependency array
  const handleVerify = useCallback(
    async (recaptchaToken) => {
      const email = formRef.current.email.value;
      const password = formRef.current.password.value;
      const body = { email, password, recaptchaToken };
      try {
        const response = await apiFetch(
          `${BASE_URL}/authUser/login`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
          null // No token is sent for the login request itself
        );

        if (response.ok) {
          const data = await response.json();

          //          addNotification('Login successful! Redirecting...', 'success');

          // --- 2FA Handling ---
          if (data.twoFactorRequired) {
            addNotification(data.message, 'info'); // Backend message is already translated
            setRequires2FA(true);
            setTwoFactorUserId(data.userId);
            setTwoFactorEmailMasked(data.emailMasked);
            setTwoFactorPhoneNumber(data.phoneNumber);
            setHasAuthenticator(data.hasAuthenticator);
            setTwoFactorSetupToken(data.twoFactorSetupToken);
            setIsSubmitting(false); // Stop password submission spinner
            return; // Stop further execution
          }
          // --- End 2FA Handling ---

          handleLoginSuccess(data);
        } else {
          const errorData = await response.json();
          reset(); // Reset reCAPTCHA on login failure
          if (response.status === 409) {
            // Specific error for Google accounts trying to use password form
            addNotification(errorData.error, 'info', 0, true); // Backend message is already translated
          } else {
            // Generic error for other login failures
            addNotification(t('loginPage.loginFailed', { error: errorData.error || t('loginPage.genericError') }), 'error', 5000);
          }
        }
      } catch (error) {
        reset(); // Also reset on network errors
        setError(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [addNotification, handleLoginSuccess, reset, setError, t]
  );

  // Keep the ref updated with the latest version of the handler.
  useEffect(() => {
    handleVerifyRef.current = handleVerify;
  }, [handleVerify]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const email = formRef.current.email.value;
    const password = formRef.current.password.value;
    if (!email || !password) {
      addNotification(t('loginPage.emailPasswordRequired'), 'warning');
      return;
    }
    //    addNotification('Verifying, please wait...', 'info');
    setIsSubmitting(true);
    execute();
  };

  const handleReset = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
    navigate(`/${lang}`); // Navigate to the home page
  };

  return (
    <div className="login-page-container">
      <div className={`login-container ${requires2FA ? 'two-factor-active' : ''}`}>
        {requires2FA ? (
          <TwoFactorAuth userId={twoFactorUserId} phoneNumber={twoFactorPhoneNumber} emailMasked={twoFactorEmailMasked} hasAuthenticator={hasAuthenticator} setupToken={twoFactorSetupToken} onLoginSuccess={handleLoginSuccess} />
        ) : (
          <>
            <h2>{t('loginPage.title')}</h2>
            <form onSubmit={handleSubmit} ref={formRef}>
              <div className="form-group">
                <label htmlFor="email">{t('loginPage.emailLabel')}</label>
                <input type="email" id="email" name="email" required autoComplete="email" />
              </div>

              <div className="form-group">
                <label htmlFor="password">{t('loginPage.passwordLabel')}</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'} // Dynamically set type
                    id="password"
                    name="password"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('loginPage.hidePassword') : t('loginPage.showPassword')}
                    title={showPassword ? t('loginPage.hidePassword') : t('loginPage.showPassword')}
                  >
                    <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                  </button>
                </div>
              </div>

              {/* Forgot Username/Password Links */}
              <div className="forgot-links">
                <Link to={`/${lang}/forgot-password`}>{t('loginPage.forgotPasswordLink')}</Link>
              </div>

              <div className="button-group">
                <button type="button" className="cancel-btn" onClick={handleReset} disabled={isSubmitting}>
                  {t('loginPage.cancelButton')}
                </button>
                <button type="submit" className="subscribe-btn" disabled={isSubmitting}>
                  {isSubmitting ? t('loginPage.loggingInButton') : t('loginPage.loginButton')}
                </button>
              </div>
              {!isReactSnap && (
                <Suspense fallback={null}>
                  <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={onChange} onErrored={onErrored} onExpired={onExpired} />
                </Suspense>
              )}
            </form>
            <div className="divider">
              <span>{t('loginPage.orDivider')}</span>
            </div>
            {isReactSnap ? (
              <button className="google-login-btn" disabled>
                <img src={googleLogo} alt={t('loginPage.googleLogoAlt')} /> {t('loginPage.signInWithGoogle')}
              </button>
            ) : (
              <a href={`${BASE_URL}/authUser/google`} className="google-login-btn">
                <img src={googleLogo} alt={t('loginPage.googleLogoAlt')} /> {t('loginPage.signInWithGoogle')}
              </a>
            )}
            <div className="register-link">
              <span>{t('loginPage.noAccount')} </span>
              <Link to={`/${lang}/subscribe`}>{t('loginPage.registerLink')}</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(Login);
